import { z } from 'zod';

// v1 knowledge: facts with source / date / validity, discrepancies between sources (both values held until a person
// picks one) and «Preguntas por resolver» grouped per project (backend: knowledge/facts.ts).

const Id = z.string().min(1).max(64);
const Iso = z.string();

export const FACT_SOURCE_TYPES = ['document', 'instruction', 'team_answer', 'manual'] as const;
export const FactSourceTypeSchema = z.enum(FACT_SOURCE_TYPES);
export const FACT_SOURCE_LABELS: Record<(typeof FACT_SOURCE_TYPES)[number], string> = {
  document: 'Documento',
  instruction: 'Instrucciones del proyecto',
  team_answer: 'Respuesta del equipo',
  manual: 'Captura manual',
};
export const FACT_STATUSES = ['active', 'held', 'superseded'] as const;
export const FACT_STATUS_LABELS: Record<(typeof FACT_STATUSES)[number], string> = { active: 'Vigente', held: 'Retenido por discrepancia', superseded: 'Reemplazado' };

export const KnowledgeFactSchema = z.object({
  id: Id,
  projectId: Id,
  key: z.string(),
  label: z.string(),
  value: z.string(),
  sourceType: FactSourceTypeSchema,
  sourceLabel: z.string(),
  sourceRef: z.string().nullable(),
  sourceDate: Iso,
  validUntil: Iso.nullable(),
  status: z.enum(FACT_STATUSES),
  /** Past its validity: LIA does not use it. */
  expired: z.boolean(),
  discrepancyId: Id.nullable(),
  createdAt: Iso,
});
export type KnowledgeFact = z.infer<typeof KnowledgeFactSchema>;

export const CreateKnowledgeFactSchema = z.object({
  label: z.string().trim().min(2).max(200),
  value: z.string().trim().min(1).max(1000),
  /** Optional stable key (defaults to the normalized label): the same key from two sources is compared. */
  key: z.string().trim().max(120).optional(),
  sourceType: z.enum(['document', 'instruction', 'manual']),
  sourceLabel: z.string().trim().min(2).max(200),
  sourceRef: z.string().trim().max(200).optional(),
  sourceDate: Iso.optional(),
  validUntil: Iso.optional(),
});
export type CreateKnowledgeFact = z.infer<typeof CreateKnowledgeFactSchema>;

export const KnowledgeDiscrepancySchema = z.object({
  id: Id,
  projectId: Id,
  key: z.string(),
  label: z.string(),
  status: z.enum(['open', 'resolved']),
  facts: z.array(KnowledgeFactSchema),
  resolvedFactId: Id.nullable(),
  resolvedNote: z.string().nullable(),
  resolvedAt: Iso.nullable(),
  createdAt: Iso,
});
export type KnowledgeDiscrepancy = z.infer<typeof KnowledgeDiscrepancySchema>;
export const DiscrepancyListQuerySchema = z.object({ status: z.enum(['open', 'resolved', 'any']).default('open') });
export const ResolveDiscrepancySchema = z.object({ factId: Id, note: z.string().trim().max(500).optional() });

export const PENDING_QUESTION_CASE_STATUSES = ['waiting', 'answer_available', 'answered'] as const;
export const PENDING_QUESTION_CASE_LABELS: Record<(typeof PENDING_QUESTION_CASE_STATUSES)[number], string> = {
  waiting: 'Esperando respuesta',
  answer_available: 'Respuesta lista para enviar',
  answered: 'Ya respondido',
};

export const PendingQuestionSchema = z.object({
  id: Id,
  projectId: Id,
  question: z.string(),
  status: z.enum(['open', 'answered']),
  answer: z.string().nullable(),
  answeredAt: Iso.nullable(),
  answeredByName: z.string().nullable(),
  waitingCount: z.number().int(),
  cases: z.array(
    z.object({
      id: Id,
      leadInterestId: Id,
      contactId: Id,
      contactName: z.string().nullable(),
      askedText: z.string(),
      status: z.enum(PENDING_QUESTION_CASE_STATUSES),
      answeredScope: z.enum(['case', 'project']).nullable(),
      answer: z.string().nullable(),
      /** Who told the prospect (lia), when known. */
      answeredBy: z.string().nullable(),
      answeredAt: Iso.nullable(),
      createdAt: Iso,
    }),
  ),
  createdAt: Iso,
});
export type PendingQuestion = z.infer<typeof PendingQuestionSchema>;
export const PendingQuestionListQuerySchema = z.object({ status: z.enum(['open', 'answered', 'any']).default('open') });
export const AnswerPendingQuestionSchema = z
  .object({
    answer: z.string().trim().min(1).max(1000),
    /** «Solo este caso» (caseId required) or «Guardar en la base del proyecto». */
    scope: z.enum(['case', 'project']),
    caseId: Id.optional(),
  })
  .refine((v) => v.scope === 'project' || !!v.caseId, { message: 'Elige la oportunidad a la que aplica la respuesta', path: ['caseId'] });
export type AnswerPendingQuestion = z.infer<typeof AnswerPendingQuestionSchema>;
